import { Filter, X } from 'lucide-react'

export interface FilterChip {
  id: string
  label: string
  onRemove: () => void
}

interface DashboardFilterBarProps {
  /** One chip per selected value across every dimension (built by the caller). */
  chips: FilterChip[]
  /** Reset the whole filter → the KPI cards return to the district totals. */
  onClear: () => void
  /** Apply the filter to the Issues board and navigate there. */
  onShow: () => void
}

/**
 * The dashboard filter tray — sits under the KPI cards, visible only while something is selected.
 * Shows every selected value (age / importance / type / department / HOD / site / taluka) as a
 * removable chip, a **Clear** (resets the KPI cards to their originals), and a **Show issues →**
 * that carries the filter into the Issues board.
 */
export function DashboardFilterBar({ chips, onClear, onShow }: DashboardFilterBarProps) {
  if (chips.length === 0) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[12px] border border-[#c7d2ea] bg-[#f4f7fd] px-3 py-2">
      <span className="flex items-center gap-1.5 text-[10.5px] font-bold tracking-wider text-gis-navy uppercase">
        <Filter className="h-[13px] w-[13px]" aria-hidden /> Filters
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {chips.map((chip) => (
          <span
            key={chip.id}
            className="flex items-center gap-1 rounded-full bg-white px-2 py-[3px] text-[11px] font-semibold text-gis-ink-2 ring-1 ring-gis-line"
          >
            <span className="max-w-[160px] truncate">{chip.label}</span>
            <button
              type="button"
              onClick={chip.onRemove}
              aria-label={`Remove ${chip.label}`}
              className="grid h-[14px] w-[14px] flex-none place-items-center rounded-full text-gis-soft transition-colors hover:bg-gis-track hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60"
            >
              <X className="h-[10px] w-[10px]" />
            </button>
          </span>
        ))}
      </div>
      <div className="ml-auto flex flex-none items-center gap-2">
        <button
          type="button"
          onClick={onClear}
          className="rounded-[8px] px-2.5 py-[5px] text-[11.5px] font-semibold text-gis-soft transition-colors hover:bg-white hover:text-gis-ink focus-visible:ring-2 focus-visible:ring-blue-500/60"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={onShow}
          className="rounded-[8px] bg-gis-navy px-3 py-[5px] text-[11.5px] font-bold text-white transition-[filter] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-blue-500/60"
        >
          Show issues →
        </button>
      </div>
    </div>
  )
}
